<template>
  <section class="google-auth-modal__step step-digit-code__step">
    <div class="step-digit-code__wrapper">
      <div
        :class="[
          'step-digit-code__digits-wrapper',
          !isModal && 'auth-form__digits-wrapper',
        ]"
      >
        <input
          v-for="n in digits"
          :class="[
            'step-digit-code__digit',
            n === currentDigitPosition &&
              !isDigitsCompleted &&
              'step-digit-code__digit--active',
            !isModal && 'auth-form__digit',
          ]"
          :key="`digit-${n}`"
          :id="n"
          :ref="`ga-digit-${n}`"
          @input="moveNextDigit($event)"
          @paste="onPaste"
          @keydown="backspaceDigit"
          :value="currentDigits[n - 1]"
          type="number"
          max="1"
        />
      </div>
    </div>
    <div
      :class="[
        'google-auth-modal__step-btn-wrapper',
        !isModal && 'auth-form__actions-group auth-form__actions-group-digit',
      ]"
    >
      <button
        @click.prevent="$parent.$emit('resend-mfa-code')"
        class="btn btn--tertiary auth-form__resend-code "
        v-if="!isGoogleAuthenticatorMFA && !isLoading && !isModal"
      >
        Resend Code
      </button>
      <button
        class="btn btn--primary google-auth-modal__step-next-btn step-digit-code__confirm-btn"
        :class="[!isModal && 'auth-form__actions-submit-wrapper']"
        @click="submitCode()"
        @keydown="backspaceDigit"
        ref="confirm"
        :disabled="isSaveDisabled"
      >
        {{ confirmBtnLabel }}
      </button>
      <button
        v-if="isModal"
        type="submit"
        class="btn btn--secondary google-auth-modal__step-next-btn step-digit-code__back-btn"
        @click="$emit('go-prev-step')"
      >
        Back
      </button>
    </div>
    <ActionLoader class="modal__loader" v-if="isLoading && isModal" />
  </section>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'DigitCodeInput',
  components: {
    ActionLoader,
  },
  props: {
    gaQrCode: {
      type: String,
      required: false,
    },
    isModal: {
      type: Boolean,
      required: false,
      default() {
        return false
      },
    },
    isGoogleAuthenticatorMFA: {
      type: Boolean,
      required: false,
      default() {
        return false
      },
    },
  },
  data() {
    return {
      isSaveDisabled: true,
      isDigitsCompleted: false,
      isLoading: false,
      currentDigitPosition: 1,
      currentDigits: [],
      digits: 6,
    }
  },
  watch: {
    currentDigitPosition() {
      const fullilledDigits = this.currentDigits?.filter(
        digit => digit !== null
      ).length
      const isDigitsCompleted = fullilledDigits === this.digits
      this.isDigitsCompleted = isDigitsCompleted
      this.isSaveDisabled = !isDigitsCompleted
    },
  },
  computed: {
    qrImage() {
      return `data:image/svg+xml;base64,${this.gaQrCode}`
    },
    confirmBtnLabel() {
      return this.isModal ? 'Confirm' : 'Confirm code'
    },
  },
  methods: {
    backspaceDigit($event) {
      if ($event.keyCode === 8) {
        const targetNumber = Number($event.currentTarget.id)
          ? Number($event.currentTarget.id) - 1
          : this.digits
        const currentDigitIndex = targetNumber || 1
        this.currentDigits[currentDigitIndex] = null
        this.$refs[`ga-digit-${currentDigitIndex}`][0].focus()
        this.currentDigitPosition = currentDigitIndex
      }
    },
    moveNextDigit($event) {
      if ($event && $event.inputType === 'insertFromPaste') {
        $event.preventDefault()
        return
      }
      const currentDigit = $event.currentTarget.value
      const currentDigitPosition = Number($event.currentTarget.id)
      this.$refs[`ga-digit-${currentDigitPosition}`][0].value = ''
      this.currentDigitPosition = currentDigitPosition
      if (currentDigit.length > 1) currentDigit.substr(0, 1)
      const currentDigitIndex = this.currentDigitPosition - 1
      const isValid = /\d/.test(currentDigit)
      this.currentDigits[currentDigitIndex] = isValid ? currentDigit : null
      if (!isValid) return
      if (this.currentDigits[currentDigitIndex]?.length > 1) {
        this.currentDigits[currentDigitIndex] = this.currentDigits[
          currentDigitIndex
        ][1]
      }
      this.currentDigitPosition++
      if (this.currentDigitPosition < this.digits + 1) {
        this.$refs[`ga-digit-${this.currentDigitPosition}`][0].focus()
      } else {
        this.$nextTick(() => this.$refs.confirm.focus())
      }
    },
    onPaste($event) {
      const pastedData = $event.clipboardData
        .getData('text')
        ?.replace(/\D/g, '')
      if (pastedData) {
        const numbers = pastedData.split('')
        for (let index = 0; index < this.digits; index++) {
          this.$refs[`ga-digit-${index + 1}`][0].value = numbers[index]
            ? numbers[index]
            : null
        }
        $event.preventDefault()
        this.currentDigits = numbers.slice(0, 6)
        this.currentDigitPosition = this.currentDigits.length + 1
        if (this.currentDigitPosition < 6)
          this.$refs[`ga-digit-${this.currentDigitPosition}`][0]?.focus()
      }
      if (this.currentDigitPosition === this.digits + 1) {
        this.$nextTick(() => this.$refs.confirm.focus())
      }
    },
    async submitCode() {
      this.isLoading = true
      this.$emit('is-digit-code-loading', true)
      this.$parent.$emit('is-digit-code-loading', true)
      this.isSaveDisabled = true
      const code = this.currentDigits.join('')
      try {
        await this.$store.dispatch('auth/submitMFACode', code)
        if (this.isModal) {
          this.$parent.$emit('close-ga-modal')
        } else {
          this.$parent.$emit('handle-submitted-form')
        }
      } catch (e) {
        this.currentDigits = []
        this.$emit('is-digit-code-loading', false)
        this.$parent.$emit('is-digit-code-loading', false)
        this.isLoading = false
        this.$nextTick(() => this.$refs[`ga-digit-1`][0].focus())
        this.currentDigitPosition = 1
      }
    },
  },
  async mounted() {
    this.$refs[`ga-digit-1`][0].focus()
  },
}
</script>

<style lang="scss" scoped>
.step-digit-code {
  &__step {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &__digits-wrapper {
    display: flex;
    justify-content: center;
    margin: 40px 0 0;
  }
  &__digit {
    width: 41px;
    height: 57px;
    border-radius: 10px;
    margin-right: 10px;
    padding: 11px;
    font-size: rem(24);
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }
    &:last-child {
      margin-right: 0;
    }
    &--active {
      border: 1px solid var(--chetwode-blue);
      box-shadow: 0px 0px 3px 2px var(--chetwode-blue);
    }
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    @include mobile {
      margin: 30px 0 0;
    }
  }
  &__input {
    max-width: 100%;
    input {
      height: 38px;
      padding: 11px;
      @include mobile {
        height: 46px;
      }
    }
  }
  &__confirm-btn {
    margin: 0;
  }
  &__back-btn {
    margin: 20px 0 50px;
  }
}
</style>
